import React, { useCallback, useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import {
  FluentProvider,
  webLightTheme,
  Spinner,
  Toast,
  ToastTitle,
  Toaster,
  useToastController,
  ToastIntent,
  Theme,
} from '@fluentui/react-components';

import Dashboard from '@/pages/Dashboard';
import Login from '@/pages/Login';
import { apiClient } from '@/services/api';
import { AuthUser } from '@/types/api';
import { useAppStore } from '@/stores/chatbotStore';
import './App.css';

// Alpilink Groupe brand theme — bleu royal primaire, surfaces claires
const appTheme: Theme = {
  ...webLightTheme,
  colorBrandBackground: '#304697',
  colorBrandBackgroundHover: '#283C7E',
  colorBrandBackgroundPressed: '#1F2F66',
  colorBrandForegroundLink: '#304697',
  colorBrandForegroundLinkHover: '#25387A',
  colorNeutralBackground1: '#FFFFFF',
  colorNeutralBackground2: '#F3F3F3',
  colorNeutralBackground3: '#E8E8EC',
  colorNeutralStroke1: '#DADCE3',
  colorNeutralStroke2: '#EAEBEF',
  fontFamilyBase: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  borderRadiusMedium: '12px',
  borderRadiusLarge: '16px',
  borderRadiusXLarge: '24px',
};

const AppContent: React.FC = () => {
  const { error, chatError, uploadError, clearError } = useAppStore();
  const { dispatchToast } = useToastController('global-toaster');

  // Show toast notifications for errors
  useEffect(() => {
    if (error) {
      dispatchToast(
        <Toast>
          <ToastTitle>Erreur</ToastTitle>
          {error}
        </Toast>,
        { intent: 'error' as ToastIntent, timeout: 5000 }
      );
      clearError();
    }
  }, [error, dispatchToast, clearError]);

  useEffect(() => {
    if (chatError) {
      dispatchToast(
        <Toast>
          <ToastTitle>Erreur de Chat</ToastTitle>
          {chatError}
        </Toast>,
        { intent: 'error' as ToastIntent, timeout: 5000 }
      );
      clearError();
    }
  }, [chatError, dispatchToast, clearError]);

  useEffect(() => {
    if (uploadError) {
      dispatchToast(
        <Toast>
          <ToastTitle>Erreur d'Upload</ToastTitle>
          {uploadError}
        </Toast>,
        { intent: 'error' as ToastIntent, timeout: 5000 }
      );
      clearError();
    }
  }, [uploadError, dispatchToast, clearError]);

  return (
    <div className="app-container">
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      <Toaster toasterId="global-toaster" />
    </div>
  );
};

const App: React.FC = () => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [checking, setChecking] = useState(true);

  const refresh = useCallback(async () => {
    try {
      setUser(await apiClient.getCurrentUser());
    } catch {
      setUser(null);
    } finally {
      setChecking(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Any 401 from the API means the session died: fall back to the login screen
  useEffect(() => apiClient.onUnauthorized(() => setUser(null)), []);

  return (
    <FluentProvider theme={appTheme}>
      {checking ? (
        <div className="app-loading">
          <Spinner label="Chargement…" />
        </div>
      ) : user ? (
        <AppContent />
      ) : (
        <Login onSignedIn={setUser} />
      )}
    </FluentProvider>
  );
};

export default App;